import React, { Suspense, lazy } from 'react';
import { CircularProgress, Box } from '@mui/material';

// Lazy load widgets for better code splitting
const ShortcutsWidget = lazy(() => import('./ShortcutsWidget'));
const RecentFilesWidget = lazy(() => import('./RecentFilesWidget'));
const QuickActionsWidget = lazy(() => import('./QuickActionsWidget'));
const GoogleCalendarWidget = lazy(() => import('./GoogleCalendarWidget'));
const UnifiProtectWidget = lazy(() => import('./UnifiProtectWidget'));
const WiimWidget = lazy(() => import('./WiimWidget'));
const NotesWidget = lazy(() => import('./NotesWidget'));
const NewsWidget = lazy(() => import('./NewsWidget'));
const HelpWidget = lazy(() => import('./HelpWidget'));
const AmazonWidget = lazy(() => import('../../widgets/AmazonWidget'));
const UpcomingMeetingsWidget = lazy(() => import('./UpcomingMeetingsWidget'));

// Widget loading wrapper
const WidgetLoadingWrapper = ({ children }) => (
  <Suspense fallback={
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
      <CircularProgress size={24} />
    </Box>
  }>
    {children}
  </Suspense>
);

// Registry of all available widgets
export const widgetTypes = [
  {
    type: 'notes',
    title: 'Notes',
    description: 'Create and manage personal notes and to-dos',
    icon: 'note',
    requiredPermission: 'notes:read',
    defaultSettings: {
      limit: 5,
      filterType: 'all',
      showCompleted: true
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of notes to display',
        min: 1,
        max: 20
      },
      filterType: {
        type: 'select',
        label: 'Filter by type',
        options: [
          { value: 'all', label: 'All' },
          { value: 'note', label: 'Notes only' },
          { value: 'todo', label: 'To-dos only' }
        ]
      },
      showCompleted: {
        type: 'boolean',
        label: 'Show completed to-dos'
      }
    }
  },
  {
    type: 'shortcuts',
    title: 'Popular Shortcuts',
    description: 'Display most frequently used shortcuts',
    icon: 'link',
    requiredPermission: 'shortcuts:read',
    defaultSettings: {
      limit: 5
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of shortcuts to display',
        min: 1,
        max: 10
      }
    }
  },
  {
    type: 'recentFiles',
    title: 'Recent Files',
    description: 'Display recently accessed Google Drive files',
    icon: 'description',
    requiredPermission: 'googleDrive:read',
    defaultSettings: {
      limit: 5
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of files to display',
        min: 1,
        max: 10
      }
    }
  },
  {
    type: 'quickActions',
    title: 'Quick Actions',
    description: 'Display quick access buttons to various features',
    icon: 'apps',
    defaultSettings: {
      enabledActions: [
        'shortcuts', 'drive', 'canva', 'glpi', 'planningCenter', 'synology', 
        'unifiAccess', 'unifiNetwork', 'unifiProtect', 'dreo', 'mosyleBusiness', 
        'lenelS2NetBox', 'googleCalendar', 'googleDrive', 'appleBusinessManager',
        'buildingManagement', 'googleAdmin', 'googleForms', 'lgThinq', 'marketplace',
        'people', 'radius', 'roomBooking', 'staffDirectory', 'tasks', 'wiim', 'amazon'
      ]
    },
    settingsSchema: {
      enabledActions: {
        type: 'multiselect',
        label: 'Enabled actions',
        options: [
          { value: 'shortcuts', label: 'Shortcuts' },
          { value: 'drive', label: 'Drive Files' },
          { value: 'canva', label: 'Canva' },
          { value: 'glpi', label: 'GLPI' },
          { value: 'planningCenter', label: 'Planning Center' },
          { value: 'synology', label: 'Synology' },
          { value: 'unifiAccess', label: 'Unifi Access' },
          { value: 'unifiNetwork', label: 'Unifi Network' },
          { value: 'unifiProtect', label: 'Unifi Protect' },
          { value: 'dreo', label: 'Dreo' },
          { value: 'mosyleBusiness', label: 'Mosyle Business' },
          { value: 'lenelS2NetBox', label: 'Lenel S2 NetBox' },
          { value: 'googleCalendar', label: 'Google Calendar' },
          { value: 'googleDrive', label: 'Google Drive' },
          { value: 'appleBusinessManager', label: 'Apple Business Manager' },
          { value: 'buildingManagement', label: 'Building Management' },
          { value: 'googleAdmin', label: 'Google Admin' },
          { value: 'googleForms', label: 'Google Forms' },
          { value: 'lgThinq', label: 'LG ThinQ' },
          { value: 'marketplace', label: 'Marketplace' },
          { value: 'people', label: 'People' },
          { value: 'radius', label: 'Radius' },
          { value: 'roomBooking', label: 'Room Booking' },
          { value: 'staffDirectory', label: 'Staff Directory' },
          { value: 'tasks', label: 'Tasks' },
          { value: 'wiim', label: 'CSF Building Music' },
          { value: 'amazon', label: 'Amazon Orders' }
        ]
      }
    }
  },
  {
    type: 'googleCalendar',
    title: 'Upcoming Events',
    description: 'Display upcoming events from Google Calendar',
    icon: 'event',
    requiredPermission: 'googleCalendar:read',
    defaultSettings: {
      limit: 5,
      calendarIds: ["ukcsf.org_j90m8br74feod2qa62llsenr0g@group.calendar.google.com", "ukcsf.org_7etm0dajuha3amo3ktpacp6vos@group.calendar.google.com", "c_qoq414dfhm96eapul5i4req78s@group.calendar.google.com"],
      viewMode: 'calendar',
      calendarView: 'compact'
    },
    settingsSchema: {
      viewMode: {
        type: 'select',
        label: 'View mode',
        options: [
          { value: 'list', label: 'List View' },
          { value: 'calendar', label: 'Calendar View' }
        ]
      },
      calendarView: {
        type: 'select',
        label: 'Calendar style',
        options: [
          { value: 'full', label: 'Full Month' },
          { value: 'compact', label: 'Compact (Day by Day)' }
        ],
        dependsOn: 'viewMode',
        showWhen: { viewMode: 'calendar' }
      },
      limit: {
        type: 'number',
        label: 'Number of events to display',
        min: 1,
        max: 10,
        dependsOn: 'viewMode',
        showWhen: { viewMode: 'list' }
      },
      calendarIds: {
        type: 'multiselect',
        label: 'Calendars to display',
        options: [], // This will be populated dynamically
        async: true,
        asyncOptionsUrl: '/api/google-calendar/calendars',
        asyncOptionMapping: {
          value: 'id',
          label: 'summary'
        }
      }
    }
  },
  {
    type: 'unifiProtect',
    title: 'Camera Feeds',
    description: 'Display camera feeds from Unifi Protect',
    icon: 'videocam',
    requiredPermission: 'unifiProtect:read',
    defaultSettings: {
      limit: 4,
      cameraIds: []
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of cameras to display',
        min: 1,
        max: 8
      },
      cameraIds: {
        type: 'multiselect',
        label: 'Specific cameras to display (leave empty for all)',
        options: [], // This will be populated dynamically
        async: true,
        asyncOptionsUrl: '/api/unifi-protect/cameras',
        asyncOptionMapping: {
          value: 'id',
          label: 'name'
        }
      }
    }
  },
  {
    type: 'wiim',
    title: 'CSF Building Music',
    description: 'Control Building Music and see what\'s playing',
    icon: 'music_note',
    requiredPermission: 'wiim:read',
    defaultSettings: {
      refreshInterval: 10
    },
    settingsSchema: {
      refreshInterval: {
        type: 'number',
        label: 'Refresh interval (seconds)',
        min: 5,
        max: 60
      }
    }
  },
  {
    type: 'news',
    title: 'Latest News',
    description: 'Display latest news posts from the organization',
    icon: 'article',
    requiredPermission: 'news:read',
    defaultSettings: {
      limit: 5,
      showCategory: true,
      showAuthor: true
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of news posts to display',
        min: 1,
        max: 10
      },
      showCategory: {
        type: 'boolean',
        label: 'Show category'
      },
      showAuthor: {
        type: 'boolean',
        label: 'Show author'
      }
    }
  },
  {
    type: 'amazon',
    title: 'Amazon Orders',
    description: 'Display Amazon order statistics and recent purchases',
    icon: 'shopping_cart',
    requiredPermission: 'amazon:read',
    defaultSettings: {
      showSummary: true,
      showRecentOrders: true
    },
    settingsSchema: {
      showSummary: {
        type: 'boolean',
        label: 'Show order summary statistics'
      },
      showRecentOrders: {
        type: 'boolean',
        label: 'Show recent orders'
      }
    }
  },
  {
    type: 'help',
    title: 'Help & Support',
    description: 'Display help articles and quick support access',
    icon: 'help_outline',
    requiredPermission: null, // Available to all users
    defaultSettings: {
      showSearch: true,
      showFeatured: true,
      showPopular: true,
      limit: 5
    },
    settingsSchema: {
      showSearch: {
        type: 'boolean',
        label: 'Show search bar'
      },
      showFeatured: {
        type: 'boolean',
        label: 'Show featured articles'
      },
      showPopular: {
        type: 'boolean',
        label: 'Show popular articles'
      },
      limit: {
        type: 'number',
        label: 'Number of articles to display',
        min: 3,
        max: 10
      }
    }
  },
  {
    type: 'upcomingMeetings',
    title: 'Upcoming Meetings',
    description: 'Display your upcoming meetings with quick access to join',
    icon: 'groups',
    requiredPermission: 'meetings:read',
    defaultSettings: {
      limit: 5,
      showRsvpStatus: true,
      showMeetingType: true,
      showLocation: true
    },
    settingsSchema: {
      limit: {
        type: 'number',
        label: 'Number of meetings to display',
        min: 1,
        max: 10
      },
      showRsvpStatus: {
        type: 'boolean',
        label: 'Show RSVP status'
      },
      showMeetingType: {
        type: 'boolean',
        label: 'Show meeting type'
      },
      showLocation: {
        type: 'boolean',
        label: 'Show location/virtual meeting info'
      }
    }
  }
];

// Function to get widget component by type
export const getWidgetComponent = (type) => {
  switch (type) {
    case 'notes':
      return NotesWidget;
    case 'shortcuts':
      return ShortcutsWidget;
    case 'recentFiles':
      return RecentFilesWidget;
    case 'quickActions':
      return QuickActionsWidget;
    case 'googleCalendar':
      return GoogleCalendarWidget;
    case 'unifiProtect':
      return UnifiProtectWidget;
    case 'wiim':
      return WiimWidget;
    case 'news':
      return NewsWidget;
    case 'amazon':
      return AmazonWidget;
    case 'help':
      return HelpWidget;
    case 'upcomingMeetings':
      return UpcomingMeetingsWidget;
    default:
      console.error(`Unknown widget type: ${type}`);
      return null;
  }
};

// Function to get default settings for a widget type
export const getDefaultSettings = (type) => {
  const widgetType = widgetTypes.find(w => w.type === type);
  return widgetType ? widgetType.defaultSettings : {};
};

// Function to render a widget based on its configuration
export const renderWidget = (widget, onRemove, onEdit, widgetBreakpoints) => {
  const WidgetComponent = getWidgetComponent(widget.type);

  if (!WidgetComponent) {
    return null;
  }

  return (
    <WidgetLoadingWrapper>
      <WidgetComponent
        key={widget._id}
        id={widget._id}
        title={widget.title}
        settings={widget.settings}
        onRemove={onRemove}
        onEdit={onEdit}
        widgetSize={widgetBreakpoints}
      />
    </WidgetLoadingWrapper>
  );
};

export default {
  widgetTypes,
  getWidgetComponent,
  getDefaultSettings,
  renderWidget
};
